import React, { useState, useEffect, useMemo } from 'react';
import { GitMerge, Plus, Edit, Trash2, Filter, ShieldCheck, Share2, MessageCircleMore, Zap, X } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import api from '../../services/api';
import styles from './SettingsPage.module.css';

const statusOptions = [
  { value: 'awaiting_receipt', label: 'Ожидает оприходования' },
  { value: 'awaiting_diagnosis', label: 'Ожидает диагностики' },
  { value: 'in_diagnosis', label: 'На диагностике' },
  { value: 'awaiting_repair', label: 'Ожидает ремонта' },
  { value: 'in_repair', label: 'В ремонте' },
  { value: 'awaiting_parts', label: 'Ожидание ЗИП' },
  { value: 'awaiting_qc', label: 'Ожидает контроля качества' },
  { value: 'in_qc', label: 'На контроле качества' },
  { value: 'awaiting_cleaning', label: 'Ожидает клининга' },
  { value: 'in_cleaning', label: 'На клининге' },
  { value: 'in_packaging', label: 'На упаковке' },
  { value: 'packaged', label: 'Упакован' },
  { value: 'awaiting_shipping', label: 'Ожидает отправки' },
  { value: 'returned', label: 'Возвращён в ремонт' },
  { value: 'shipped', label: 'Отправлен' },
];

const parseJsonField = (value, label) => {
  if (!value || !value.trim()) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    toast.error(`Поле «${label}» содержит некорректный JSON`);
    throw error;
  }
};

const stringifyJsonField = (value) => {
  if (!value) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
};

const getInitialProjectId = () => {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem('selected_project_id');
  if (!stored) return '';
  const parsed = Number(stored);
  return Number.isNaN(parsed) ? '' : parsed;
};

const CustomStatusIntegrationList = () => {
  const [integrations, setIntegrations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(getInitialProjectId);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    status_key: '',
    display_name: '',
    projects: [],
    customers: [],
    previous_status: '',
    next_status: '',
    integration_required: false,
    check_repair_limit: false,
    allow_partial_approval: false,
    auto_transition: false,
    is_assistant_integration: false,
    is_telegram_integration: false,
    api_endpoint: '',
    api_method: 'POST',
    request_template: '',
    request_mapping: '',
    api_headers: '',
    response_rules: '',
    expected_response_key: '',
    telegram_bot_token: '',
    telegram_webhook_url: '',
  });

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setIntegrations([]);
      return;
    }
    fetchIntegrations();
  }, [selectedProject, selectedCustomer]);

  const projectOptions = useMemo(
    () => projects.map(project => ({ value: project.id, label: project.name })),
    [projects]
  );

  const customerOptions = useMemo(
    () => customers.map(customer => ({ value: customer.id, label: customer.name })),
    [customers]
  );

  const fetchProjects = async () => {
    try {
      const { data } = await api.get('/settings/projects/');
      setProjects(data);
    } catch (error) {
      toast.error('Не удалось загрузить проекты');
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data } = await api.get('/settings/customers/');
      setCustomers(data);
    } catch (error) {
      toast.error('Не удалось загрузить заказчиков');
    }
  };

  const fetchIntegrations = async () => {
    if (!selectedProject) return;
    try {
      setLoading(true);
      const params = { project_id: selectedProject };
      if (selectedCustomer) params.customer_id = selectedCustomer;
      const { data } = await api.get('/repairs/custom-status-integrations/', { params });
      setIntegrations(data);
    } catch (error) {
      toast.error('Ошибка загрузки интеграций');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    if (!selectedProject) {
      toast.warn('Сначала выберите проект');
      return;
    }
    const defaultProjectOption = projectOptions.find(option => option.value === selectedProject);
    setFormData({
      status_key: '',
      display_name: '',
      projects: defaultProjectOption ? [defaultProjectOption] : [],
      customers: [],
      previous_status: '',
      next_status: '',
      integration_required: false,
      check_repair_limit: false,
      allow_partial_approval: false,
      auto_transition: false,
      is_assistant_integration: false,
      is_telegram_integration: false,
      api_endpoint: '',
      api_method: 'POST',
      request_template: '',
      request_mapping: '',
      api_headers: '',
      response_rules: '',
      expected_response_key: '',
      telegram_bot_token: '',
      telegram_webhook_url: '',
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.status_key.trim() || !formData.display_name.trim()) {
      toast.error('Заполните ключ и отображаемое имя');
      return;
    }
    if (formData.projects.length === 0) {
      toast.error('Выберите хотя бы один проект');
      return;
    }

    let payload;
    try {
      payload = {
        status_key: formData.status_key.trim(),
        display_name: formData.display_name.trim(),
        project_ids: formData.projects.map(option => option.value),
        customer_ids: formData.customers.map(option => option.value),
        previous_status: formData.previous_status || null,
        next_status: formData.next_status || null,
        integration_required: formData.integration_required,
        check_repair_limit: formData.check_repair_limit,
        allow_partial_approval: formData.allow_partial_approval,
        auto_transition: formData.auto_transition,
        is_assistant_integration: formData.is_assistant_integration,
        is_telegram_integration: formData.is_telegram_integration,
        api_endpoint: formData.api_endpoint || null,
        api_method: formData.api_method,
        expected_response_key: formData.expected_response_key || null,
        telegram_bot_token: formData.telegram_bot_token || null,
        telegram_webhook_url: formData.telegram_webhook_url || null,
        request_template: parseJsonField(formData.request_template, 'Шаблон запроса'),
        request_mapping: parseJsonField(formData.request_mapping, 'Маппинг запроса'),
        api_headers: parseJsonField(formData.api_headers, 'Заголовки запроса'),
        response_rules: parseJsonField(formData.response_rules, 'Правила ответа'),
      };
    } catch {
      return;
    }

    try {
      if (editingId) {
        await api.put(`/repairs/custom-status-integrations/${editingId}/`, payload);
        toast.success('Интеграция обновлена');
      } else {
        await api.post('/repairs/custom-status-integrations/', payload);
        toast.success('Интеграция создана');
      }
      closeModal();
      fetchIntegrations();
    } catch (error) {
      toast.error('Ошибка сохранения интеграции');
    }
  };

  const handleEdit = (integration) => {
    setFormData({
      status_key: integration.status_key,
      display_name: integration.display_name,
      projects: projectOptions.filter(option =>
        integration.projects.some(project => project.id === option.value)
      ),
      customers: customerOptions.filter(option =>
        integration.customers.some(customer => customer.id === option.value)
      ),
      previous_status: integration.previous_status || '',
      next_status: integration.next_status || '',
      integration_required: integration.integration_required,
      check_repair_limit: integration.check_repair_limit,
      allow_partial_approval: integration.allow_partial_approval,
      auto_transition: integration.auto_transition,
      is_assistant_integration: integration.is_assistant_integration,
      is_telegram_integration: integration.is_telegram_integration,
      api_endpoint: integration.api_endpoint || '',
      api_method: integration.api_method || 'POST',
      expected_response_key: integration.expected_response_key || '',
      telegram_bot_token: integration.telegram_bot_token || '',
      telegram_webhook_url: integration.telegram_webhook_url || '',
      request_template: stringifyJsonField(integration.request_template),
      request_mapping: stringifyJsonField(integration.request_mapping),
      api_headers: stringifyJsonField(integration.api_headers),
      response_rules: stringifyJsonField(integration.response_rules),
    });
    setEditingId(integration.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить интеграцию?')) return;
    try {
      await api.delete(`/repairs/custom-status-integrations/${id}/`);
      toast.success('Интеграция удалена');
      fetchIntegrations();
    } catch (error) {
      toast.error('Ошибка удаления интеграции');
    }
  };

  const renderBadges = (integration) => {
    const badges = [];
    if (integration.integration_required) {
      badges.push(
        <span key="api" className={`${styles.badge} ${styles.badgeInfo}`}>
          <Share2 size={12} />
          API
        </span>
      );
    }
    if (integration.is_assistant_integration) {
      badges.push(
        <span key="assistant" className={`${styles.badge} ${styles.badgeSuccess}`}>
          <ShieldCheck size={12} />
          Ассистент
        </span>
      );
    }
    if (integration.is_telegram_integration) {
      badges.push(
        <span key="telegram" className={`${styles.badge} ${styles.badgeWarning}`}>
          <MessageCircleMore size={12} />
          Telegram
        </span>
      );
    }
    if (integration.auto_transition) {
      badges.push(
        <span key="auto" className={`${styles.badge} ${styles.badgeMuted}`}>
          <Zap size={12} />
          Автопереход
        </span>
      );
    }
    return badges;
  };

  const renderList = (items) => {
    if (!items || items.length === 0) {
      return <span className={styles.mutedText}>—</span>;
    }
    return items.map(item => (
      <span key={item.id} className={styles.pill}>
        {item.name}
      </span>
    ));
  };

  const selectedProjectOption = projectOptions.find(option => option.value === selectedProject) || null;
  const selectedCustomerOption = customerOptions.find(option => option.value === selectedCustomer) || null;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <GitMerge size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Интеграции пользовательских статусов
        </h3>
        <button className={styles.addButton} onClick={openAddModal} disabled={!selectedProject}>
          <Plus size={18} />
          Добавить интеграцию
        </button>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterBlock}>
          <label>
            <Filter size={14} />
            Проект
          </label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Выберите проект"
            value={selectedProjectOption}
            onChange={(option) => setSelectedProject(option ? option.value : '')}
            options={projectOptions}
            isClearable
          />
        </div>
        <div className={styles.filterBlock}>
          <label>Заказчик</label>
          <Select
            className={styles.select}
            classNamePrefix="react-select"
            placeholder="Все заказчики"
            value={selectedCustomerOption}
            onChange={(option) => setSelectedCustomer(option ? option.value : '')}
            options={customerOptions}
            isClearable
          />
        </div>
      </div>

      {!selectedProject && (
        <div className={styles.infoBanner}>
          Выберите проект, чтобы увидеть связанные интеграции согласования.
        </div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка интеграций...</p>
          </div>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          {integrations.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <GitMerge size={48} />
              </div>
              <h3>Нет интеграций</h3>
              <p>Создайте правило, чтобы связывать статусы с внешними системами</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Ключ статуса</th>
                  <th>Переход</th>
                  <th>Интеграции</th>
                  <th>Проекты</th>
                  <th>Заказчики</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {integrations.map(integration => (
                  <tr key={integration.id} className={styles.clickableRow} onClick={() => handleEdit(integration)}>
                    <td><strong>{integration.display_name}</strong></td>
                    <td>
                      <code className={styles.inlineCode}>{integration.status_key}</code>
                    </td>
                    <td>
                      <div className={styles.transitionText}>
                        {integration.previous_status || '—'} → {integration.next_status || '—'}
                      </div>
                    </td>
                    <td className={styles.badgeColumn}>{renderBadges(integration)}</td>
                    <td className={styles.pillColumn}>{renderList(integration.projects)}</td>
                    <td className={styles.pillColumn}>{renderList(integration.customers)}</td>
                    <td>
                      <button className={styles.actionButton} onClick={(event) => { event.stopPropagation(); handleDelete(integration.id); }} title="Удалить">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <GitMerge size={20} />
                {editingId ? 'Редактировать интеграцию' : 'Создать интеграцию'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label>Ключ статуса</label>
                  <input
                    type="text"
                    value={formData.status_key}
                    onChange={(event) => setFormData({ ...formData, status_key: event.target.value })}
                    placeholder="Например, ready_for_approval"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Отображаемое имя</label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(event) => setFormData({ ...formData, display_name: event.target.value })}
                    placeholder="Название для пользователей"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Проекты</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите проекты"
                    value={formData.projects}
                    onChange={(value) => setFormData({ ...formData, projects: value || [] })}
                    options={projectOptions}
                    isMulti
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Заказчики</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите заказчиков"
                    value={formData.customers}
                    onChange={(value) => setFormData({ ...formData, customers: value || [] })}
                    options={customerOptions}
                    isMulti
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Предыдущий статус</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите статус"
                    value={statusOptions.find(option => option.value === formData.previous_status) || null}
                    onChange={(option) => setFormData({ ...formData, previous_status: option ? option.value : '' })}
                    options={statusOptions}
                    isClearable
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Следующий статус</label>
                  <Select
                    className={styles.select}
                    classNamePrefix="react-select"
                    placeholder="Выберите статус"
                    value={statusOptions.find(option => option.value === formData.next_status) || null}
                    onChange={(option) => setFormData({ ...formData, next_status: option ? option.value : '' })}
                    options={statusOptions}
                    isClearable
                  />
                </div>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.integration_required}
                    onChange={(event) => setFormData({ ...formData, integration_required: event.target.checked })}
                  />
                  Требуется запрос к API
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.check_repair_limit}
                    onChange={(event) => setFormData({ ...formData, check_repair_limit: event.target.checked })}
                  />
                  Проверять лимит ремонта
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.allow_partial_approval}
                    onChange={(event) => setFormData({ ...formData, allow_partial_approval: event.target.checked })}
                  />
                  Разрешить частичное согласование
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.auto_transition}
                    onChange={(event) => setFormData({ ...formData, auto_transition: event.target.checked })}
                  />
                  Включить автопереход
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.is_assistant_integration}
                    onChange={(event) => setFormData({ ...formData, is_assistant_integration: event.target.checked })}
                  />
                  Использовать ассистента
                </label>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.is_telegram_integration}
                    onChange={(event) => setFormData({ ...formData, is_telegram_integration: event.target.checked })}
                  />
                  Интеграция с Telegram
                </label>
                <div className={styles.formGroup}>
                  <label>API Endpoint</label>
                  <input
                    type="text"
                    value={formData.api_endpoint}
                    onChange={(event) => setFormData({ ...formData, api_endpoint: event.target.value })}
                    placeholder="https://example.com/api"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Метод API</label>
                  <select
                    value={formData.api_method}
                    onChange={(event) => setFormData({ ...formData, api_method: event.target.value })}
                  >
                    <option value="POST">POST</option>
                    <option value="GET">GET</option>
                  </select>
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Шаблон запроса (JSON)</label>
                  <textarea
                    className={styles.jsonTextarea}
                    value={formData.request_template}
                    onChange={(event) => setFormData({ ...formData, request_template: event.target.value })}
                    placeholder='{"key":"value"}'
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Маппинг запроса (JSON)</label>
                  <textarea
                    className={styles.jsonTextarea}
                    value={formData.request_mapping}
                    onChange={(event) => setFormData({ ...formData, request_mapping: event.target.value })}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Заголовки запроса (JSON)</label>
                  <textarea
                    className={styles.jsonTextarea}
                    value={formData.api_headers}
                    onChange={(event) => setFormData({ ...formData, api_headers: event.target.value })}
                  />
                </div>
                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label>Правила ответа (JSON)</label>
                  <textarea
                    className={styles.jsonTextarea}
                    value={formData.response_rules}
                    onChange={(event) => setFormData({ ...formData, response_rules: event.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Ожидаемый ключ ответа</label>
                  <input
                    type="text"
                    value={formData.expected_response_key}
                    onChange={(event) => setFormData({ ...formData, expected_response_key: event.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Telegram Bot Token</label>
                  <input
                    type="text"
                    value={formData.telegram_bot_token}
                    onChange={(event) => setFormData({ ...formData, telegram_bot_token: event.target.value })}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Telegram Webhook URL</label>
                  <input
                    type="text"
                    value={formData.telegram_webhook_url}
                    onChange={(event) => setFormData({ ...formData, telegram_webhook_url: event.target.value })}
                  />
                </div>
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Сохранить изменения' : 'Создать интеграцию'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomStatusIntegrationList;
